import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { translateTexts } from './translateApi'

const LanguageContext = createContext(null)
const STORAGE_KEY = 'slimfast_lang'
const CACHE_STORAGE_KEY = 'slimfast_i18n_cache_v3'
const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'CODE',
  'PRE',
  'TEXTAREA',
  'INPUT',
  'SELECT',
  'OPTION',
  'SVG',
  'PATH',
])

/** Theme SplitText / letter-split headings — translate as full phrases, not per letter. */
const PHRASE_SELECTOR = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  '.visible-from-right',
  '.display-four',
  '.display-five',
  '.section-title > span',
].join(',')

function readStoredLang() {
  try {
    const raw = String(localStorage.getItem(STORAGE_KEY) || 'EN').toUpperCase()
    return raw === 'AR' ? 'AR' : 'EN'
  } catch {
    return 'EN'
  }
}

function normalizePhrase(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
}

function protectBrandAndSupport(text) {
  return String(text || '')
    .replace(/سليم\s*سريع/gi, 'سليم فاست')
    .replace(/\b[Ss][Ll][Ii][Mm]\s*[Ff][Aa][Ss][Tt]\b/g, 'سليم فاست')
    .replace(/\bSlimFast\b/gi, 'سليم فاست')
}

function finalizeArabic(src, translated) {
  const phrase = normalizePhrase(src)
  const lower = phrase.toLowerCase()
  if (lower === 'support') return 'الدعم'
  if (/^slim\s*fast$/i.test(phrase) || lower === 'slimfast') return 'سليم فاست'
  const glossary =
    CLIENT_GLOSSARY[`EN:AR:${phrase}`] || CLIENT_GLOSSARY[`EN:AR:${src}`]
  if (glossary) return protectBrandAndSupport(glossary)
  return protectBrandAndSupport(translated || src)
}

function isTranslatableText(value) {
  const text = normalizePhrase(value)
  if (!text) return false
  if (text.length < 2) return false
  if (/^[\d\s.,:%$€£¥+\-–—/\\|()[\]{}]+$/.test(text)) return false
  if (/^https?:\/\//i.test(text) || /^[\w.+-]+@[\w.-]+\.\w+$/.test(text)) return false
  return true
}

function looksLatin(value) {
  const text = String(value || '')
  return /[A-Za-z]/.test(text) && !/[\u0600-\u06FF]/.test(text)
}

function collectPhraseElements(root) {
  if (!root) return []
  const all = Array.from(root.querySelectorAll(PHRASE_SELECTOR))
  return all.filter((el) => {
    if (el.closest('[data-no-translate],.no-translate')) return false
    // Never wipe structural/media containers (breaks Swiper / cards)
    if (el.querySelector('img, video, iframe, .swiper, .swiper-wrapper, canvas')) return false
    return !all.some((other) => other !== el && other.contains(el))
  })
}

function applyTranslatedPhrase(el, translated) {
  const anchors = el.querySelectorAll(':scope > a')
  if (
    anchors.length === 1 &&
    normalizePhrase(el.textContent) === normalizePhrase(anchors[0].textContent)
  ) {
    if (!el.getAttribute('data-i18n-phrase')) {
      el.setAttribute('data-i18n-html', el.innerHTML)
    }
    anchors[0].textContent = translated
    el.setAttribute('data-i18n-phrase', '1')
    return
  }

  // Letter-split / plain text headings
  if (!el.getAttribute('data-i18n-phrase')) {
    el.setAttribute('data-i18n-html', el.innerHTML)
  }
  el.textContent = translated
  el.setAttribute('data-i18n-phrase', '1')
}

function collectTextNodes(root, skipInside) {
  const nodes = []
  if (!root) return nodes

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT
      if (parent.closest('[data-no-translate],.no-translate')) return NodeFilter.FILTER_REJECT
      if (parent.closest('[data-i18n-phrase]')) return NodeFilter.FILTER_REJECT
      // Skip Swiper clones — originals are enough; cloning fights the translator
      if (parent.closest('.swiper-slide-duplicate')) return NodeFilter.FILTER_REJECT
      if (skipInside && skipInside.has(parent.closest(PHRASE_SELECTOR))) {
        return NodeFilter.FILTER_REJECT
      }
      if (!isTranslatableText(node.nodeValue)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  let current = walker.nextNode()
  while (current) {
    nodes.push(current)
    current = walker.nextNode()
  }
  return nodes
}

function chunk(list, size) {
  const out = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

function notifyLayoutRefresh(lang) {
  window.dispatchEvent(
    new CustomEvent('slimfast:language', {
      detail: { lang },
    })
  )
}

/** Client-side glossary — instant Arabic for common UI strings (no API call). */
const CLIENT_GLOSSARY = {
  // Navigation
  'EN:AR:Home': 'الرئيسية',
  'EN:AR:home': 'الرئيسية',
  'EN:AR:Home /': 'الرئيسية /',
  'EN:AR:Ingredients': 'المكونات',
  'EN:AR:About Slimfast': 'عن سليم فاست',
  'EN:AR:Shop': 'المتجر',
  'EN:AR:Shop No Sidebar': 'المتجر',
  'EN:AR:Contact': 'تواصل معنا',
  'EN:AR:We accept': 'نقبل الدفع عبر',
  'EN:AR:Our Shops': 'متاجرنا',
  'EN:AR:Sign In': 'تسجيل الدخول',
  'EN:AR:Dashboard': 'لوحة التحكم',
  'EN:AR:Cart': 'السلة',
  'EN:AR:Checkout': 'إتمام الطلب',
  // Home / products
  'EN:AR:Special Products': 'منتجات مميزة',
  'EN:AR:Perfect Brand Is Featured On': 'علامتنا التجارية مميزة في',
  'EN:AR:How It Works': 'كيف يعمل',
  'EN:AR:Our Products': 'منتجاتنا',
  'EN:AR:Shop All': 'تسوق الكل',
  'EN:AR:Best Sellers': 'الأكثر مبيعاً',
  'EN:AR:Features Products': 'منتجات مميزة',
  'EN:AR:Latest Arrlued': 'أحدث الوصول',
  'EN:AR:Shop Slim Fast Now': 'تسوق سليم فاست الآن',
  'EN:AR:Shop Offers': 'عروض المتجر',
  'EN:AR:Loading products…': 'جاري تحميل المنتجات…',
  'EN:AR:No products yet. Add them in the admin panel.': 'لا توجد منتجات بعد.',
  'EN:AR:No products yet. Add some from the admin panel.': 'لا توجد منتجات بعد.',
  'EN:AR:Sort by Newest': 'الأحدث',
  'EN:AR:Showing': 'عرض',
  'EN:AR:product': 'منتج',
  'EN:AR:products': 'منتجات',
  'EN:AR:Add to Cart': 'أضف إلى السلة',
  'EN:AR:Out of Stock': 'نفدت الكمية',
  'EN:AR:Back to Shop': 'العودة للمتجر',
  'EN:AR:Continue Shopping': 'متابعة التسوق',
  // Cart / checkout
  'EN:AR:Cart Totals': 'إجمالي السلة',
  'EN:AR:Proceed To Checkout': 'إتمام الطلب',
  'EN:AR:Subtotal': 'المجموع الفرعي',
  'EN:AR:Shipping': 'الشحن',
  'EN:AR:Total': 'الإجمالي',
  'EN:AR:Place Order': 'تأكيد الطلب',
  'EN:AR:Order Summary': 'ملخص الطلب',
  // Dashboard
  'EN:AR:Current Orders': 'الطلبات الحالية',
  'EN:AR:Order History': 'سجل الطلبات',
  'EN:AR:Wishlist': 'قائمة الأمنيات',
  'EN:AR:My Cart': 'سلتي',
  'EN:AR:Go Shopping': 'تسوق الآن',
  'EN:AR:Track Order': 'تتبع الطلب',
  'EN:AR:No orders yet.': 'لا توجد طلبات بعد.',
  // Auth
  'EN:AR:Welcome to SLIM Fast': 'مرحباً بك في سليم فاست',
  'EN:AR:Slim Fast': 'سليم فاست',
  'EN:AR:SLIM Fast': 'سليم فاست',
  'EN:AR:slim fast': 'سليم فاست',
  'EN:AR:SLIM FAST': 'سليم فاست',
  'EN:AR:Discover Slim Fast products': 'اكتشف منتجات سليم فاست',
  'EN:AR:Explore Slim Fast Products': 'اكتشف منتجات سليم فاست',
  'EN:AR:View all products': 'عرض جميع المنتجات',
  'EN:AR:View All Products': 'عرض جميع المنتجات',
  'EN:AR:Support': 'الدعم',
  'EN:AR:support': 'الدعم',
  'EN:AR:Email Address': 'البريد الإلكتروني',
  'EN:AR:Password': 'كلمة المرور',
  'EN:AR:Continue': 'متابعة',
  'EN:AR:Forgot password?': 'نسيت كلمة المرور؟',
  'EN:AR:Create Account': 'إنشاء حساب',
  'EN:AR:Full Name': 'الاسم الكامل',
  // Footer
  'EN:AR:Shop & Account': 'المتجر والحساب',
  'EN:AR:Contact Info': 'معلومات التواصل',
  'EN:AR:Quick Links': 'روابط سريعة',
  'EN:AR:About Us': 'من نحن',
  'EN:AR:Privacy Policy': 'سياسة الخصوصية',
  'EN:AR:Terms of Use': 'شروط الاستخدام',
  // Status
  'EN:AR:delivered': 'تم التوصيل',
  'EN:AR:out for delivery': 'قيد التوصيل',
  'EN:AR:ready for shipment': 'جاهز للشحن',
  'EN:AR:cancelled': 'ملغي',
}

function readTranslationCache() {
  const merged = new Map(Object.entries(CLIENT_GLOSSARY))
  try {
    const stored = localStorage.getItem(CACHE_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([k, v]) => merged.set(k, v))
      }
    }
  } catch { /* ignore */ }
  return merged
}

function persistTranslationCache(cache) {
  try {
    const obj = {}
    cache.forEach((v, k) => { if (!CLIENT_GLOSSARY[k]) obj[k] = v })
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(obj))
  } catch { /* ignore */ }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() =>
    typeof window !== 'undefined' ? readStoredLang() : 'EN'
  )
  const [busy, setBusy] = useState(false)
  const textOriginalsRef = useRef(new WeakMap())
  const cacheRef = useRef(readTranslationCache())
  const translatingRef = useRef(false)
  const langRef = useRef(lang)
  langRef.current = lang

  const applyDocumentLang = useCallback((next) => {
    const html = document.documentElement
    html.lang = next === 'AR' ? 'ar' : 'en'
    html.dir = next === 'AR' ? 'rtl' : 'ltr'
    html.setAttribute('data-lang', next)
  }, [])

  const setLang = useCallback((nextRaw) => {
    const next = String(nextRaw || '').toUpperCase() === 'AR' ? 'AR' : 'EN'
    setLangState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const restoreEnglish = useCallback(() => {
    const root = document.getElementById('root')
    if (!root) return

    root.querySelectorAll('[data-i18n-phrase]').forEach((el) => {
      const html = el.getAttribute('data-i18n-html')
      if (html != null) el.innerHTML = html
      el.removeAttribute('data-i18n-phrase')
      el.removeAttribute('data-i18n-html')
    })

    const nodes = collectTextNodes(root, null)
    nodes.forEach((node) => {
      const original = textOriginalsRef.current.get(node)
      if (original != null) node.nodeValue = original
    })
  }, [])

  const translatePage = useCallback(async (target) => {
    if (translatingRef.current) return
    const root = document.getElementById('root')
    if (!root) return

    translatingRef.current = true
    setBusy(true)
    try {
      if (target === 'EN') {
        restoreEnglish()
        notifyLayoutRefresh('EN')
        return
      }

      const phrases = collectPhraseElements(root)
      const phraseJobs = []
      const phraseSet = new Set(phrases)

      for (const el of phrases) {
        const phrase = normalizePhrase(el.textContent)
        if (!isTranslatableText(phrase) || !looksLatin(phrase)) continue
        const cacheKey = `EN:AR:${phrase}`
        if (cacheRef.current.has(cacheKey)) {
          applyTranslatedPhrase(el, finalizeArabic(phrase, cacheRef.current.get(cacheKey)))
        } else {
          phraseJobs.push({ el, phrase })
        }
      }

      if (phraseJobs.length) {
        const unique = [...new Set(phraseJobs.map((j) => j.phrase))]
        for (const batch of chunk(unique, 25)) {
          const translated = await translateTexts(batch, 'AR', 'EN')
          batch.forEach((src, i) => {
            const out = finalizeArabic(src, translated[i])
            cacheRef.current.set(`EN:AR:${src}`, out)
            cacheRef.current.set(`EN:AR:${normalizePhrase(src)}`, out)
          })
        }
        phraseJobs.forEach(({ el, phrase }) => {
          applyTranslatedPhrase(
            el,
            finalizeArabic(phrase, cacheRef.current.get(`EN:AR:${phrase}`) ?? phrase),
          )
        })
      }

      const nodes = collectTextNodes(root, phraseSet)
      const pending = []

      for (const node of nodes) {
        const current = node.nodeValue
        if (!looksLatin(current)) continue

        if (!textOriginalsRef.current.has(node)) {
          textOriginalsRef.current.set(node, current)
        }
        const original = textOriginalsRef.current.get(node)
        const cacheKey = `EN:AR:${normalizePhrase(original)}`
        if (cacheRef.current.has(cacheKey)) {
          node.nodeValue = finalizeArabic(original, cacheRef.current.get(cacheKey))
        } else {
          pending.push({ node, original, key: cacheKey })
        }
      }

      if (pending.length) {
        const unique = [...new Set(pending.map((p) => normalizePhrase(p.original)))]
        for (const batch of chunk(unique, 40)) {
          const translated = await translateTexts(batch, 'AR', 'EN')
          batch.forEach((src, i) => {
            const out = finalizeArabic(src, translated[i])
            cacheRef.current.set(`EN:AR:${src}`, out)
          })
        }
        pending.forEach(({ node, original, key }) => {
          node.nodeValue = finalizeArabic(
            original,
            cacheRef.current.get(key) ??
              cacheRef.current.get(`EN:AR:${normalizePhrase(original)}`) ??
              original,
          )
        })
      }

      // Swiper loop clones keep English — sync them from cache
      root.querySelectorAll('.swiper-slide-duplicate').forEach((slide) => {
        const walker = document.createTreeWalker(slide, NodeFilter.SHOW_TEXT)
        let node = walker.nextNode()
        while (node) {
          const raw = node.nodeValue
          if (looksLatin(raw) && isTranslatableText(raw)) {
            const key = `EN:AR:${normalizePhrase(raw)}`
            if (cacheRef.current.has(key)) {
              node.nodeValue = finalizeArabic(raw, cacheRef.current.get(key))
            }
          }
          node = walker.nextNode()
        }
        slide.querySelectorAll(PHRASE_SELECTOR).forEach((el) => {
          const phrase = normalizePhrase(el.textContent)
          if (!looksLatin(phrase)) return
          const key = `EN:AR:${phrase}`
          if (cacheRef.current.has(key)) {
            applyTranslatedPhrase(el, finalizeArabic(phrase, cacheRef.current.get(key)))
          }
        })
      })

      notifyLayoutRefresh('AR')
      persistTranslationCache(cacheRef.current)
    } catch (err) {
      console.error('Language API translate failed:', err)
    } finally {
      translatingRef.current = false
      setBusy(false)
    }
  }, [restoreEnglish])

  useEffect(() => {
    applyDocumentLang(lang)
    const timer = window.setTimeout(() => {
      translatePage(lang)
    }, 200)
    return () => window.clearTimeout(timer)
  }, [lang, applyDocumentLang, translatePage])

  useEffect(() => {
    const root = document.getElementById('root')
    if (!root) return undefined

    let debounce
    const observer = new MutationObserver((mutations) => {
      if (langRef.current !== 'AR' || translatingRef.current) return

      const meaningful = mutations.some((m) => {
        if (!m.addedNodes?.length) return false
        for (const node of m.addedNodes) {
          if (!(node instanceof Element) && !(node instanceof Text)) continue
          const el = node instanceof Element ? node : node.parentElement
          if (!el) continue
          if (el.closest('[data-no-translate],.no-translate')) continue
          if (el.closest('.swiper-slide-duplicate')) continue
          // New product cards / dynamic content (including inside Swiper slides)
          if (looksLatin(el.textContent) && isTranslatableText(el.textContent)) return true
        }
        return false
      })
      if (!meaningful) return

      window.clearTimeout(debounce)
      debounce = window.setTimeout(() => {
        if (langRef.current === 'AR') translatePage('AR')
      }, 500)
    })

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: false,
    })

    return () => {
      observer.disconnect()
      window.clearTimeout(debounce)
    }
  }, [translatePage])

  const refreshTranslations = useCallback(() => {
    if (langRef.current === 'AR') {
      window.setTimeout(() => translatePage('AR'), 100)
    }
  }, [translatePage])

  const value = useMemo(
    () => ({
      lang,
      setLang,
      busy,
      refreshTranslations,
      isArabic: lang === 'AR',
      isEnglish: lang === 'EN',
    }),
    [lang, setLang, busy, refreshTranslations]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}
